import type { CompetitionSummaryDto, SubmissionStatus } from '@napaicp/schemas'
import { COMPETITION_ROLES, type GlobalRole } from '@napaicp/shared'
import {
  getCompetitionNextMilestone,
  type CompetitionScheduleDates,
} from '@/features/public/competition-lifecycle'
import {
  deriveStudentJourneyStage,
  submissionWindowPhaseForStatus,
} from '@/features/student/derive-student-journey'
import { homeForCompetition } from '@/lib/role-routing'

/**
 * The single place that turns a multi-role account's raw data (competitions
 * + roles + per-competition status) into "what do I need to do right now" —
 * every function here is pure (no fetching, no JSX, no i18n) so it is unit
 * testable without mocking `fetch` or rendering anything. `use-home-overview.ts`
 * is the only caller; UI components only ever render the `HomeAction`/
 * `HomeWorkspace`/`RecentCompetitionViewModel` shapes below, never branch on
 * role themselves.
 */

export type ActionPriority = 'p0' | 'p1' | 'p2'

const PRIORITY_ORDER: Record<ActionPriority, number> = { p0: 0, p1: 1, p2: 2 }

/** Within 7 days counts as "即將截止" (P1); already past counts as P0 (clock-skew/edge-transition case — rare, since the server's own lifecycle status normally moves the competition out of the actionable state once a deadline passes). */
const URGENT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000

export function priorityForDeadline(deadline: string | null, now: Date): ActionPriority {
  if (deadline === null) return 'p2'
  const diffMs = new Date(deadline).getTime() - now.getTime()
  if (diffMs < 0) return 'p0'
  if (diffMs <= URGENT_WINDOW_MS) return 'p1'
  return 'p2'
}

export type HomeActionKind =
  | 'admin_publish'
  | 'judge_pending'
  | 'platform_governance'
  | 'student_registration'
  | 'student_registration_update'
  | 'student_team'
  | 'student_submission'

export interface HomeAction {
  key: string
  kind: HomeActionKind
  priority: ActionPriority
  /** `null` for the one platform-level action, which has no single competition. */
  competitionId: string | null
  competitionName: string | null
  /** ISO timestamp, or `null` when the action has no deadline to show/sort by. */
  deadline: string | null
  /** Set only for count-based actions (judge pending, platform governance queue). */
  count: number | undefined
  ctaTo: string
}

export function sortActions(actions: readonly HomeAction[]): HomeAction[] {
  return [...actions].sort((a, b) => {
    const byPriority = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]
    if (byPriority !== 0) return byPriority
    if (a.deadline === null && b.deadline === null) return 0
    if (a.deadline === null) return 1
    if (b.deadline === null) return -1
    return new Date(a.deadline).getTime() - new Date(b.deadline).getTime()
  })
}

// ---------------------------------------------------------------------------
// Per-action derivation
// ---------------------------------------------------------------------------

/** Zero extra fetch: `status`/`roles` are already on `CompetitionSummaryDto`. */
export function deriveAdminPublishAction(
  competition: Pick<CompetitionSummaryDto, 'id' | 'name' | 'status' | 'roles'>,
): HomeAction | null {
  const canPublish =
    competition.roles.includes(COMPETITION_ROLES.COMPETITION_ADMIN) ||
    competition.roles.includes(COMPETITION_ROLES.STAFF)
  if (!canPublish || competition.status !== 'draft') return null
  return {
    key: `admin-publish-${competition.id}`,
    kind: 'admin_publish',
    priority: 'p2',
    competitionId: competition.id,
    competitionName: competition.name,
    deadline: null,
    count: undefined,
    ctaTo: `/admin/competitions/${competition.id}/overview`,
  }
}

export function deriveJudgeAction(
  competition: Pick<CompetitionSummaryDto, 'id' | 'name'>,
  pendingCount: number,
): HomeAction | null {
  if (pendingCount <= 0) return null
  return {
    key: `judge-${competition.id}`,
    kind: 'judge_pending',
    priority: 'p2',
    competitionId: competition.id,
    competitionName: competition.name,
    deadline: null,
    count: pendingCount,
    ctaTo: `/judge/assignments?competitionId=${competition.id}`,
  }
}

/** The only platform-governance signal `/home` surfaces — see the plan's scope note on why last-active-super-admin risk and judge-conflict count (both on `/platform`) are deliberately excluded here. */
export function derivePlatformGovernanceAction(pendingCount: number): HomeAction | null {
  if (pendingCount <= 0) return null
  return {
    key: 'platform-governance',
    kind: 'platform_governance',
    priority: 'p2',
    competitionId: null,
    competitionName: null,
    deadline: null,
    count: pendingCount,
    ctaTo: '/platform/competition-requests?status=submitted',
  }
}

export interface StudentActionInput {
  competitionId: string
  competitionName: string
  teamRequired: boolean
  registrationHasForm: boolean
  registrationCompleted: boolean
  /** `null` when `!teamRequired` — never consulted in that case. */
  hasTeam: boolean | null
  /** Latest submission status for the caller's team, or `null` if none exists yet. */
  submissionStatus: SubmissionStatus | null
  /** PR-258/BR-191: `true` when the latest submission's `submittedAt` is non-null — see `StudentJourneyInput.wasSubmittedBefore`. */
  wasSubmittedBefore: boolean
  /** PR-264 (Wave SJ-5 API delta): from the latest submission's `isFinalRound` (`SubmissionListItemDto`) — real round context, not a guess. */
  isFinalRound: boolean
  schedule: CompetitionScheduleDates
}

/**
 * PR-186: the priority order itself (registration -> team -> submission ->
 * waiting/done) is no longer decided here — it defers to the same
 * `deriveStudentJourneyStage` `student-competition-page.tsx` and
 * `participant-cta.ts` use, via `schedule.status` (this hook only ever calls
 * `deriveStudentAction` for a competition in `registration_open`/
 * `submission_open`/`judging` — see `ACTIVE_STATUSES` in
 * `use-home-overview.ts` — so `submissionWindowPhaseForStatus` and
 * `competitionCompleted`/`resultPublished` below are effectively always
 * `false`/non-`result_published`, but the shared function is still the one
 * source of truth rather than a second copy of the same three booleans).
 * Only `registration`/`team`/`submission_start`/`submission_continue`/
 * `submission_ready` stages produce a Home action; every other stage
 * (nothing to do yet, window
 * closed with nothing submitted, already submitted, or an unreachable
 * completed/result stage this hook's competition filter excludes) means
 * this competition contributes none — deliberately including two cases the
 * old three-boolean version got wrong: it used to keep prompting
 * "submission" both before the window opened and after a missed deadline.
 */
export function deriveStudentAction(input: StudentActionInput, now: Date): HomeAction | null {
  const stage = deriveStudentJourneyStage({
    registrationRequired: input.registrationHasForm,
    registrationCompleted: input.registrationCompleted,
    teamRequired: input.teamRequired,
    hasTeam: input.hasTeam === true,
    submissionStatus: input.submissionStatus,
    wasSubmittedBefore: input.wasSubmittedBefore,
    submissionWindowPhase: submissionWindowPhaseForStatus(
      input.schedule.status,
      {
        submissionStartAt: input.schedule.submissionStartAt ?? null,
        submissionEndAt: input.schedule.submissionEndAt,
      },
      now,
    ),
    competitionCompleted: input.schedule.status === 'completed',
    resultPublished: false,
    // PR-264 (Wave SJ-5 API delta): `mySubmissionsQueryOptions` now returns
    // `SubmissionListItemDto` (real round context from the server), so this
    // is no longer a guess.
    isFinalRound: input.isFinalRound,
  })

  const milestone = getCompetitionNextMilestone(input.schedule)
  const deadline = milestone?.at ?? null
  const base = {
    competitionId: input.competitionId,
    competitionName: input.competitionName,
    deadline,
    count: undefined,
    priority: priorityForDeadline(deadline, now),
  } as const

  switch (stage) {
    case 'registration':
      return {
        ...base,
        key: `student-registration-${input.competitionId}`,
        kind: 'student_registration',
        ctaTo: `/student/competitions/${input.competitionId}`,
      }
    case 'team':
      return {
        ...base,
        key: `student-team-${input.competitionId}`,
        kind: 'student_team',
        ctaTo: `/student/competitions/${input.competitionId}/team`,
      }
    // PR-258/BR-191: a reopened submission (`correction_required`) is
    // exactly as actionable as a fresh one — same CTA target, the
    // Submission page itself renders the distinct correction copy.
    // PR-264 (Wave SJ-5): the final-round relabels of the three
    // submission-in-progress stages are exactly as actionable as their
    // preliminary-round counterparts — same CTA target, same Submission
    // page (BR-197: final round reuses the exact same Submission
    // Workbench). Before this fix, `isFinalRound` was always hardcoded
    // `false` here so these three cases were unreachable dead code; now
    // that real round context flows through, a student who has advanced to
    // the final round and still needs to submit would otherwise silently
    // get no Home action at all — a real regression this fix closes before
    // it could ship.
    case 'submission_start':
    case 'submission_continue':
    case 'submission_ready':
    case 'correction_required':
    case 'final_submission_start':
    case 'final_submission_continue':
    case 'final_submission_ready':
      return {
        ...base,
        key: `student-submission-${input.competitionId}`,
        kind: 'student_submission',
        ctaTo: `/student/competitions/${input.competitionId}/submission`,
      }
    // Nothing to do yet (window hasn't opened), nothing actionable anymore
    // (window closed with no entry), already submitted, or an
    // unreachable completed/result stage — this hook only ever calls
    // `deriveStudentAction` for a competition in `registration_open`/
    // `submission_open`/`judging` (see `ACTIVE_STATUSES` in
    // `use-home-overview.ts`). `final_awaiting_judging` joins
    // `awaiting_judging` here for the same reason.
    // `submission_expired` (BR-206/AC-318): never actually submitted and the
    // window is closed — nothing left to action, same bucket as
    // `submission_missed`.
    // Session 7 (item 3): `final_submission_expired` joins
    // `submission_expired` here for the same reason — never actually
    // submitted, window closed, nothing actionable.
    case 'submission_not_open':
    case 'submission_missed':
    case 'submission_expired':
    case 'final_submission_expired':
    case 'awaiting_judging':
    case 'final_awaiting_judging':
    case 'competition_done':
    case 'submission_locked':
    case 'submission_locked_done':
    case 'result_published':
      return null
  }
}

// ---------------------------------------------------------------------------
// Workspaces ("我的工作區")
// ---------------------------------------------------------------------------

export type HomeWorkspaceKey = 'participate' | 'manage' | 'judge' | 'platform'

export interface HomeWorkspace {
  key: HomeWorkspaceKey
  count: number
  to: string
}

/**
 * One card per capability the caller actually holds — never a role switcher,
 * never every card for everyone. `isPlatformAdmin` also grants the "manage"
 * card even with zero explicit `competition_admin` grants, mirroring the
 * pre-existing quick-action behavior (`/admin/competitions` already renders
 * safely empty for a super admin with nothing to manage yet).
 */
export function deriveWorkspaces(
  competitions: readonly Pick<CompetitionSummaryDto, 'roles'>[],
  isPlatformAdmin: boolean,
  platformPendingCount: number,
): HomeWorkspace[] {
  const workspaces: HomeWorkspace[] = []

  const participateCount = competitions.filter(
    (c) =>
      c.roles.includes(COMPETITION_ROLES.STUDENT) || c.roles.includes(COMPETITION_ROLES.TEACHER),
  ).length
  if (participateCount > 0) {
    workspaces.push({ key: 'participate', count: participateCount, to: '/student' })
  }

  const manageCount = competitions.filter(
    (c) =>
      c.roles.includes(COMPETITION_ROLES.COMPETITION_ADMIN) ||
      c.roles.includes(COMPETITION_ROLES.STAFF),
  ).length
  if (manageCount > 0 || isPlatformAdmin) {
    workspaces.push({ key: 'manage', count: manageCount, to: '/admin/competitions' })
  }

  const judgeCount = competitions.filter((c) => c.roles.includes(COMPETITION_ROLES.JUDGE)).length
  if (judgeCount > 0) {
    workspaces.push({ key: 'judge', count: judgeCount, to: '/judge/assignments' })
  }

  if (isPlatformAdmin) {
    workspaces.push({ key: 'platform', count: platformPendingCount, to: '/platform' })
  }

  return workspaces
}

// ---------------------------------------------------------------------------
// Recent competitions
// ---------------------------------------------------------------------------

export interface RecentCompetitionViewModel {
  id: string
  name: string
  status: string
  shortDescription: string | null
  roleBadges: readonly string[]
  /** ISO timestamp of the next milestone, or `null` when there isn't one (e.g. `completed`, or schedule not loaded). */
  nextMilestoneAt: string | null
  ctaTo: string
}

export function deriveRecentCompetitionViewModel(
  competition: Pick<CompetitionSummaryDto, 'id' | 'name' | 'status' | 'shortDescription' | 'roles'>,
  schedule: CompetitionScheduleDates | null,
  globalRole: GlobalRole,
): RecentCompetitionViewModel {
  return {
    id: competition.id,
    name: competition.name,
    status: competition.status,
    shortDescription: competition.shortDescription,
    roleBadges: competition.roles,
    nextMilestoneAt: schedule === null ? null : (getCompetitionNextMilestone(schedule)?.at ?? null),
    ctaTo: homeForCompetition(globalRole, competition.roles, competition.id),
  }
}

export interface RegistrationUpdateActionInput {
  competitionId: string
  competitionName: string
  /** Labels of required fields the saved registration no longer satisfies. */
  incompleteLabels: readonly string[]
  schedule: CompetitionScheduleDates
}

/**
 * AC-373: an extra Home action, in addition to the student's usual first-step
 * action, for a saved registration that no longer satisfies the current form
 * (a required field was added, or a required checkbox was saved unchecked).
 * Display only: the journey stage that decides the usual action is untouched.
 * The deadline is the registration deadline, the last day they can still fix it.
 */
export function deriveRegistrationUpdateAction(
  input: RegistrationUpdateActionInput,
  now: Date,
): HomeAction | null {
  if (input.incompleteLabels.length === 0) return null
  const deadline = input.schedule.registrationEndAt
  return {
    key: `student-registration-update-${input.competitionId}`,
    kind: 'student_registration_update',
    priority: priorityForDeadline(deadline, now),
    competitionId: input.competitionId,
    competitionName: input.competitionName,
    deadline,
    count: undefined,
    ctaTo: `/student/competitions/${input.competitionId}`,
  }
}
